import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Truck,
  CheckCircle,
  XCircle,
  Phone,
  MapPin,
  Calendar,
  Search,
  RefreshCw,
  Navigation,
  Clock,
} from "lucide-react";

export default function CasaMatrizDespachos() {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [updatingEstado, setUpdatingEstado] = useState(null);

  useEffect(() => {
    fetchDespachos();
  }, []);

  const fetchDespachos = async () => {
    try {
      const res = await api.get("/pedidos-terceros?estado=en_transito");
      setPedidos(res.data);
    } catch (error) {
      toast.error("Error al cargar despachos");
    } finally {
      setLoading(false);
    }
  };

  const marcarEntregado = async (pedidoId) => {
    setUpdatingEstado(pedidoId);
    try {
      await api.put(`/pedidos-terceros/${pedidoId}/estado`, { estado: "entregado" });
      toast.success("Pedido marcado como entregado");
      fetchDespachos();
    } catch (error) {
      toast.error("Error al actualizar");
    } finally {
      setUpdatingEstado(null);
    }
  };

  const marcarCancelado = async (pedidoId) => {
    setUpdatingEstado(pedidoId);
    try {
      await api.put(`/pedidos-terceros/${pedidoId}/estado`, { estado: "cancelado" });
      toast.success("Pedido cancelado");
      fetchDespachos();
    } catch (error) {
      toast.error("Error al actualizar");
    } finally {
      setUpdatingEstado(null);
    }
  };

  const abrirMapa = (direccion) => {
    const encoded = encodeURIComponent(direccion);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encoded}`, "_blank");
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const filteredPedidos = pedidos.filter(p =>
    p.cliente_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.cliente_direccion?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group by delivery time
  const groupedByTime = filteredPedidos.reduce((acc, p) => {
    const time = p.hora_entrega || "Sin hora";
    if (!acc[time]) acc[time] = [];
    acc[time].push(p);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-purple-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="casa-matriz-despachos">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Despachos en Ruta</h1>
          <p className="text-torta-muted">
            {pedidos.length} pedidos en tránsito
          </p>
        </div>
        <Button onClick={fetchDespachos} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Buscar por cliente o dirección..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {pedidos.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center">
          <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-display text-xl text-torta-dark mb-2">Sin despachos en ruta</h3>
          <p className="text-torta-muted">No hay pedidos en tránsito actualmente</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByTime).sort().map(([time, pedidosGrupo]) => (
            <div key={time}>
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-purple-600" />
                <h3 className="font-medium text-torta-dark">Entrega: {time}</h3>
                <span className="text-sm text-torta-muted">({pedidosGrupo.length} pedidos)</span>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pedidosGrupo.map(pedido => (
                  <div key={pedido.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-3 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Truck className="w-5 h-5" />
                          <span className="font-medium">{pedido.cliente_nombre}</span>
                        </div>
                        <span className="text-sm opacity-80">{pedido.total_unidades} und</span>
                      </div>
                    </div>
                    
                    <div className="p-4 space-y-3">
                      <div className="flex items-start gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-torta-brown flex-shrink-0 mt-0.5" />
                        <p className="text-torta-muted">{pedido.cliente_direccion}</p>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="w-4 h-4 text-torta-brown" />
                        <a href={`tel:${pedido.cliente_telefono}`} className="text-torta-brown hover:underline">
                          {pedido.cliente_telefono}
                        </a>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <span className="font-display text-lg text-torta-brown">
                          {formatCurrency(pedido.total)}
                        </span>
                        {pedido.tipo_recaudo === "contra_entrega" && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                            Cobrar
                          </span>
                        )}
                        {pedido.tipo_recaudo === "pago_vendedor" && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                            Pagado
                          </span>
                        )}
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => abrirMapa(pedido.cliente_direccion)}
                          className="flex-1"
                        >
                          <Navigation className="w-4 h-4 mr-1" />
                          Mapa
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => marcarEntregado(pedido.id)}
                          disabled={updatingEstado === pedido.id}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Entregado
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
